import * as React from "react";
import { Dispatch } from "redux";
import { DragDropContext } from "react-dnd";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import HTML5Backend from "react-dnd-html5-backend";
import update from "immutability-helper";

import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import { ConnectedTableItem } from "./table-item";
import {
	ConnectedAddTableDialog,
	AddTableDialogData
} from "./add-table-dialog";
import { SnackbarVariant } from "../../actions/store/type";
import { setSnackbarMessage } from "../../actions/store/snackbar-actions";
import { Table } from "../../api/tables";

interface State {
	tables: ReadonlyArray<Table>;
	editTableIndex: number;
	addTableDialogOpen: boolean;
}

export interface TableListProps {
	onRef: (instance: TableList) => void;
}

interface ConnectedTableListProps extends WithStyles<typeof styles> {
	tables: ReadonlyArray<Table>;
	setSnackbarMessage: (message: string, variant?: SnackbarVariant) => void;
}

type Props = TableListProps & ConnectedTableListProps & InjectedTranslateProps;

export class TableList extends React.Component<Props, State> {
	public state: State = {
		addTableDialogOpen: false,
		editTableIndex: null,
		tables: this.sortTables(this.props.tables)
	};

	public componentDidMount() {
		this.props.onRef(this);
	}

	private sortTables(tables: ReadonlyArray<Table>) {
		const t = Array.from(tables);

		t.sort((a, b) => {
			return a.sortIndex - b.sortIndex;
		});

		return t;
	}

	private moveTable = (dragIndex: number, hoverIndex: number) => {
		const { tables } = this.state;
		const dragTable = tables[dragIndex];

		this.setState(
			update(this.state, {
				tables: {
					$splice: [[dragIndex, 1], [hoverIndex, 0, dragTable]]
				}
			})
		);
	};

	private handleDialogClose = () => {
		this.setState({
			addTableDialogOpen: false,
			editTableIndex: null
		});
	};

	private handleDialogSubmit = (
		data: AddTableDialogData,
		isEditing: boolean,
		index: number
	) => {
		const { tables } = this.state;

		if (tables === null || tables.length === 0) {
			this.setState({
				tables: [
					{
						...data,
						sortIndex: 0,
						layoutX: 0,
						layoutY: 0,
						layoutWidth: 0,
						layoutHeight: 0
					}
				]
			});
		} else if (isEditing) {
			this.setState({
				tables: update(tables, {
					[index]: {
						$set: {
							...this.state.tables[index],
							...data
						}
					}
				})
			});
		} else if (tables.every(table => table.key !== data.key)) {
			this.setState({
				tables: update(tables, {
					$push: [
						{
							...data,
							sortIndex: 0,
							layoutX: 0,
							layoutY: 0,
							layoutWidth: 0,
							layoutHeight: 0
						}
					]
				})
			});
		} else {
			this.props.setSnackbarMessage(
				"Tisch mit Key " + data.key + " existiert bereits!",
				"error"
			);
		}

		this.handleDialogClose();
	};

	private handleAddButtonClick = () => {
		this.setState({ addTableDialogOpen: true });
	};

	private handleEditTableClick = (index: number) => {
		this.setState({
			addTableDialogOpen: true,
			editTableIndex: index
		});
	};

	private handleDeleteTableClick = (index: number) => {
		this.setState({
			tables: update(this.state.tables, {
				$splice: [[index, 1]]
			})
		});
	};

	public getTables = () => {
		return this.state.tables
			.map((table, index) => ({
				...table,
				sortIndex: index
			}))
			.filter(table => table !== null);
	};

	public render() {
		const { classes } = this.props;
		const { tables, editTableIndex } = this.state;
		const editTable = tables[editTableIndex];

		return (
			<React.Fragment>
				<List>
					{this.state.tables && this.state.tables.length > 0 ? (
						this.state.tables.map((table: Table, index) => (
							<React.Fragment key={table.key}>
								<ConnectedTableItem
									index={index}
									sortIndex={index}
									areaId={table.areaId}
									displayName={table.displayName}
									peopleSoftLimit={table.peopleSoftLimit}
									moveSortable={this.moveTable}
									onEditClick={this.handleEditTableClick}
									onDeleteClick={this.handleDeleteTableClick}
								/>
								<Divider />
							</React.Fragment>
						))
					) : (
						<ListItem>
							<ListItemText
								classes={{ primary: classes.unavailableLabel }}
								primary="Noch keine Tische vorhanden!"
							/>
						</ListItem>
					)}
				</List>

				<div className={classes.buttonContainer}>
					<Button
						className={classes.addButton}
						variant="outlined"
						color="primary"
						onClick={this.handleAddButtonClick}
					>
						Hinzufügen
					</Button>
				</div>
				<ConnectedAddTableDialog
					open={this.state.addTableDialogOpen}
					id={editTable && editTable.id}
					index={editTableIndex}
					itemKey={editTable && editTable.key}
					areaId={editTable && editTable.areaId}
					displayName={editTable && editTable.displayName}
					peopleSoftLimit={editTable && editTable.peopleSoftLimit}
					onClose={this.handleDialogClose}
					onSubmit={this.handleDialogSubmit}
				/>
			</React.Fragment>
		);
	}
}

@DragDropContext(HTML5Backend)
class DecoratedTableList extends TableList {}

const mapStateToProps = (state: StoreState) => ({
	tables: state.tables
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setSnackbarMessage: (message: string, variant: SnackbarVariant) =>
		dispatch(setSnackbarMessage(message, variant))
});

const styles = (theme: Theme) =>
	createStyles({
		addButton: {},
		buttonContainer: {
			padding: theme.spacing.unit * 2,
			display: "flex",
			justifyContent: "flex-end"
		},
		unavailableLabel: {
			fontStyle: "italic",
			color: theme.palette.grey[600]
		}
	});

export const ConnectedTableList = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(DecoratedTableList)
	)
);
